import {Injectable} from '@angular/core'
import {BehaviorSubject} from 'rxjs'
import {LocalStorageService} from './local-storage.service'
import {ContextService} from './context.service'

export interface FeatureMenu {
  domainDeactivation: boolean
  betaTerminalConfiguration: boolean
  notification: boolean
  importExport: boolean
  operatingHours: boolean
  // Add other feature flags as needed
}

@Injectable({
  providedIn: 'root',
})
export class FeatureMenuService {
  private featureMenuKey = 'feature-menu'
  private featuresEnabledSubject: BehaviorSubject<FeatureMenu>

  constructor(
    private store: LocalStorageService,
    private contextSvc: ContextService,
  ) {
    this.featuresEnabledSubject = new BehaviorSubject<FeatureMenu>(
      this.initFeatureMenu(),
    )
  }

  private initFeatureMenu(): FeatureMenu {
    //let isCuroUser = this.contextSvc.isCuroUser();
    // check if the logged in user is not a curo user, if he is not - make sure
    // to overwrite some features back to false, if they were set to true in the first place
    // i.e someone logs in as curo user -> logs out -> back in as non curo user
    // ----- this needs to be replaced by the user data service to prevent issues like this --
    // ----- this is caused by using the local storage on the browser as a source of truth -----

    // load existing config from local storage
    let existingMenu = JSON.parse(this.store.getItem(this.featureMenuKey))
    // load overwrite setting
    // so with this flag we have a singular detection option to
    // change already set local storage values.
    // i.e. users that have had local storage setup before this change, will detect
    // that the flag does not exist, and thus it will change the values directly
    // and it will update the local storage to reflect the overwrite, once.
    const overwriteFlag1 = this.store.getItem('112025-overwrite-done')

    let defaultMenu: FeatureMenu = {
      domainDeactivation: false,
      betaTerminalConfiguration: true, // default enabled for now - doesnt mean the user has permision for it tho.
      notification: false,
      importExport: false,
      operatingHours: true,
      // Initialize other properties as needed
    }

    if (!overwriteFlag1) {
      if (existingMenu) {
        existingMenu.domainDeactivation = false
        existingMenu.notification = false
      }

      defaultMenu.domainDeactivation = false
      defaultMenu.notification = false
      this.store.setItem(this.featureMenuKey, JSON.stringify(existingMenu))
      this.store.setItem('112025-overwrite-done', 'true')
    }

    // Merge: keep existing settings, but add any new keys from defaultMenu
    if (existingMenu) {
      const mergedMenu: FeatureMenu = {...defaultMenu, ...existingMenu}
      this.store.setItem(this.featureMenuKey, JSON.stringify(mergedMenu))
      return mergedMenu
    } else {
      this.store.setItem(this.featureMenuKey, JSON.stringify(defaultMenu))
      return defaultMenu
    }
  }

  updateContextMenuStatus(key: keyof FeatureMenu, state: boolean): void {
    const currentMenu = this.featuresEnabledSubject.getValue()
    const updatedMenu = {...currentMenu, [key]: state}

    this.store.setItem(this.featureMenuKey, JSON.stringify(updatedMenu))
    this.featuresEnabledSubject.next(updatedMenu)
  }

  isCustomFeatureEnabled(key: keyof FeatureMenu): boolean {
    const value = this.store.getItem(this.featureMenuKey)
    if (value) {
      const featureMenu: FeatureMenu = JSON.parse(value)
      return featureMenu[key] || false // Return false if the key does not exist
    }
    return false // Return false if the value is null or undefined
  }

  get featuresEnabled$() {
    return this.featuresEnabledSubject.asObservable()
  }
}
